import {
  withValidation,
  composeSDKFactories,
  clickPropsSDKFactory,
  focusPropsSDKFactory,
  messageTemplates,
  isUrl,
  reportError,
  createElementPropsSDKFactory,
} from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import type {
  ILottieEmbedSDKFactory,
  ILottieEmbedSDK,
  ILottieEmbedCompProps,
} from '../LottieEmbed.types';
import { isValidLottieJson } from '../utils';
import { lottieSDKValidationSchema } from './LottieEmbed.validations';

const LottieEmbedSDKFactory: ILottieEmbedSDKFactory = ({
  setProps,
  props,
  compRef,
}) => {
  return {
    get speed() {
      return props.speed;
    },
    set speed(value) {
      setProps({ speed: value });
    },
    get loop() {
      return props.loop;
    },
    set loop(value) {
      setProps({ loop: value });
    },
    get autoplay() {
      return props.autoplay;
    },
    set autoplay(value) {
      setProps({ autoplay: value });
    },
    get showPlaybackControl() {
      return props.showPlaybackControl;
    },
    set showPlaybackControl(value) {
      setProps({ showPlaybackControl: value });
    },
    get src() {
      return props.animationUrl !== undefined
        ? props.animationUrl
        : props.animationJson;
    },
    set src(value) {
      switch (typeof value) {
        case 'string': {
          if (isUrl(value)) {
            setProps({ animationUrl: value, animationJson: undefined });
          } else {
            reportError(
              messageTemplates.error_invalid_url({
                url: 'Animation URL',
                type: 'json',
                prefix: 'http | https',
              }),
            );
          }
          break;
        }
        case 'object': {
          const validationErrors = isValidLottieJson(value);
          if (validationErrors.length === 0) {
            setProps({ animationUrl: undefined, animationJson: value });
          } else {
            reportError(
              messageTemplates.error_object_bad_format({
                keyName: validationErrors[0].property,
                propertyName: 'src',
                functionName: 'set src',
                wrongValue: 'src',
                message:
                  'The expected object structure should be a valid lottie animation file - https://lottiefiles.com/what-is-lottie',
              }),
            );
          }
          break;
        }
        default:
          reportError(
            messageTemplates.error_type({
              propertyName: 'json',
              functionName: 'object or string',
              value,
              expectedType: 'object or string',
            }),
          );
          break;
      }
    },
    get direction() {
      return props.direction;
    },
    set direction(value) {
      setProps({ direction: value });
    },
    get alt() {
      return props.alt;
    },
    set alt(value) {
      setProps({ alt: value });
    },
    play() {
      compRef.play();
    },
    stop() {
      compRef.stop();
    },
    pause() {
      compRef.pause();
    },
  };
};
const LottieEmbedSDK = withValidation(
  LottieEmbedSDKFactory,
  lottieSDKValidationSchema,
);

export const sdk = composeSDKFactories<ILottieEmbedCompProps, ILottieEmbedSDK>([
  createElementPropsSDKFactory(),
  LottieEmbedSDK,
  clickPropsSDKFactory,
  focusPropsSDKFactory,
]);

export default createComponentSDKModel(sdk);
